import { useCallback, useEffect, useRef, useState } from "react";
import { isEditableEventTarget, normalizeTour, prefersReducedMotion, presentationKeyAction, tourTransitionDuration, viewportForSize } from "../utils/presentationTour.js";

export default function usePresentationController({ tour, getViewport, setViewport, getViewportSize, onExit }) {
  const normalizedTour = normalizeTour(tour);
  const stops = normalizedTour.stops;
  const [mode, setMode] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cameraMoving, setCameraMoving] = useState(false);
  const [fullscreen, setFullscreen] = useState(() => typeof document !== "undefined" && Boolean(document.fullscreenElement));
  const modeRef = useRef(null);
  const fallbackViewportSizeRef = useRef(null);
  const freeViewportSizeRef = useRef(null);
  const cameraMoveTimerRef = useRef(null);

  useEffect(() => { modeRef.current = mode; }, [mode]);

  const moveTo = useCallback((index) => {
    if (!stops.length) return;
    const nextIndex = Math.max(0, Math.min(stops.length - 1, index));
    setCurrentIndex(nextIndex);
    const currentSize = getViewportSize?.();
    const resolved = viewportForSize(
      stops[nextIndex].viewport,
      stops[nextIndex].viewport_size || fallbackViewportSizeRef.current,
      currentSize
    );
    if (resolved) {
      const duration = prefersReducedMotion() ? 0 : tourTransitionDuration(normalizedTour.transition_speed);
      if (cameraMoveTimerRef.current) window.clearTimeout(cameraMoveTimerRef.current);
      setCameraMoving(duration > 0);
      setViewport(resolved, { duration });
      if (duration > 0) {
        cameraMoveTimerRef.current = window.setTimeout(() => {
          cameraMoveTimerRef.current = null;
          setCameraMoving(false);
        }, duration + 32);
      }
    }
  }, [getViewportSize, normalizedTour.transition_speed, setViewport, stops]);

  const startFree = useCallback(() => {
    freeViewportSizeRef.current = getViewportSize?.() || null;
    setMode("free");
  }, [getViewportSize]);
  const startGuided = useCallback((index = 0) => {
    if (!stops.length) return;
    fallbackViewportSizeRef.current = getViewportSize?.() || null;
    setMode("guided");
    moveTo(index);
  }, [getViewportSize, moveTo, stops.length]);
  const exit = useCallback(() => {
    if (cameraMoveTimerRef.current) {
      window.clearTimeout(cameraMoveTimerRef.current);
      cameraMoveTimerRef.current = null;
    }
    setCameraMoving(false);
    setViewport(getViewport(), { duration: 0 });
    freeViewportSizeRef.current = null;
    setMode(null);
    onExit?.();
  }, [getViewport, onExit, setViewport]);
  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      // Fullscreen can be unavailable when the browser or embedding context disallows it.
    }
  }, []);
  const next = useCallback(() => moveTo(currentIndex + 1), [currentIndex, moveTo]);
  const previous = useCallback(() => moveTo(currentIndex - 1), [currentIndex, moveTo]);

  useEffect(() => {
    if (!mode) return undefined;
    const onKeyDown = (event) => {
      if (isEditableEventTarget(event.target)) return;
      const keyAction = presentationKeyAction(event.key);
      if (keyAction === "exit") {
        event.preventDefault();
        exit();
        return;
      }
      if (keyAction === "fullscreen") {
        event.preventDefault();
        toggleFullscreen();
        return;
      }
      if (modeRef.current !== "guided") return;
      const action = {
        next: () => moveTo(currentIndex + 1),
        previous: () => moveTo(currentIndex - 1),
        first: () => moveTo(0),
        last: () => moveTo(stops.length - 1),
      }[keyAction];
      if (!action) return;
      event.preventDefault();
      action();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [currentIndex, exit, mode, moveTo, stops.length, toggleFullscreen]);

  useEffect(() => {
    const update = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", update);
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);

  useEffect(() => {
    if (!mode) return undefined;
    let frame = null;
    const recenter = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = null;
        if (mode === "guided") {
          moveTo(currentIndex);
          return;
        }
        const previousSize = freeViewportSizeRef.current;
        const currentSize = getViewportSize?.() || null;
        freeViewportSizeRef.current = currentSize;
        if (!previousSize || !currentSize) return;
        const resolved = viewportForSize(getViewport(), previousSize, currentSize);
        if (resolved) setViewport(resolved, { duration: 0 });
      });
    };
    window.addEventListener("resize", recenter);
    document.addEventListener("fullscreenchange", recenter);
    return () => {
      window.removeEventListener("resize", recenter);
      document.removeEventListener("fullscreenchange", recenter);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [currentIndex, getViewport, getViewportSize, mode, moveTo, setViewport]);

  useEffect(() => () => {
    if (cameraMoveTimerRef.current) window.clearTimeout(cameraMoveTimerRef.current);
  }, []);

  return {
    active: Boolean(mode), mode, currentIndex, stops, getViewport, cameraMoving,
    startFree, startGuided, exit, next, previous, moveTo, fullscreen, toggleFullscreen,
  };
}
